import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { getApiService } from '../services/api'
import type { AuthUser } from '../services/api'

type AuthState = {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  loginWithGoogle: () => void
  loginWithPassword: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshMe: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshMe = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await getApiService().me()
      setUser(response.data.user)
    } catch (_err) {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshMe()
  }, [refreshMe])

  const loginWithGoogle = useCallback(() => {
    const returnTo = `${window.location.origin}/dashboard`
    window.location.href = getApiService().getGoogleAuthUrl(returnTo)
  }, [])

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    const response = await getApiService().login(email, password)
    setUser(response.data.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      await getApiService().logout()
    } finally {
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthState>(() => ({
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    loginWithGoogle,
    loginWithPassword,
    logout,
    refreshMe,
  }), [isLoading, loginWithGoogle, loginWithPassword, logout, refreshMe, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuthContext must be used within AuthProvider')
  return context
}

export function AuthGuard() {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuthContext()

  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />
  return <Outlet />
}
